import { Link } from "@inertiajs/react"
import { Check } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { Button } from "@/components/ui/button"
import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { creatorMissionsPath } from "@/routes"

export interface SuccessArrivalProps {
  /** Inviting brand display name — drives the heading + logo fallback. */
  brandName: string
  /** Signed brand logo URL, or null → `<BrandLogo>` monogram fallback. */
  brandLogoUrl: string | null
  /** Full-bleed brand cover URL, or null → dark brand-accent scrim only
   *  (UX Spec §2 / PRD Q5 graceful degradation). */
  coverImageUrl: string | null
  /** The freshly-signed-up creator's name (welcome copy). */
  creatorName: string
  testId?: string
}

/**
 * OKS-458 — §02 immersive arrival ("You're on {brand}'s crew").
 *
 * The full-screen branded *moment* a creator lands on right after
 * creating their account (UX Spec §2 / Figma `25:83`). Composition:
 *   - a full-bleed brand **cover** layer (or a dark brand-accent scrim
 *     when no cover is attached — the `onError` swap also degrades a
 *     stale signed URL gracefully),
 *   - a **dark scrim** over the cover for on-dark text legibility,
 *   - a centred white **logo card** with an accent **check badge**,
 *   - the heading "You're on {brand}'s crew" + a personalised welcome,
 *   - a full-width "Get started" CTA that hands off to
 *     `creatorMissionsPath()` (js-routes — never a hardcoded URL), where
 *     the onboarding wizard enforcer takes over (OKS-469 — softened
 *     copy so the CTA no longer implies the mission is one tap away).
 *
 * # Motion
 *
 * The centred content fades/scales in once via the shared `brand-intro`
 * motion beat (CSS-only, ≤480ms, single play). The `motion-safe:` prefix
 * is baked into the beat, so `prefers-reduced-motion` users get the
 * arrival statically with no entrance animation (UX Spec §4 / §6).
 *
 * # Accessibility
 *
 * The heading receives focus on mount (UX Spec §6 reading order). The
 * cover image is decorative (`alt=""`); the check badge carries an
 * accessible label. The CTA is a real keyboard-reachable link.
 *
 * # Brand isolation
 *
 * Brand name / logo / cover + the creator's own name are passed as
 * props — no IDs/slugs, no other brand's data.
 */
export function SuccessArrival({
  brandName,
  brandLogoUrl,
  coverImageUrl,
  creatorName,
  testId = "signup-arrival",
}: SuccessArrivalProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [coverBroken, setCoverBroken] = useState(false)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  const showCover = Boolean(coverImageUrl) && !coverBroken

  return (
    <div
      data-testid={testId}
      data-state="success"
      className="bg-primary relative flex min-h-svh flex-col overflow-hidden"
    >
      {/* Full-bleed brand cover (or the dark brand-accent scrim alone
          when absent / failed to load). */}
      {showCover && (
        <img
          src={coverImageUrl!}
          alt=""
          className="absolute inset-0 size-full object-cover"
          data-testid={`${testId}-cover`}
          onError={() => setCoverBroken(true)}
        />
      )}
      {/* Dark scrim over the cover for on-dark text legibility. */}
      <div
        className="absolute inset-0 bg-black/60"
        data-testid={`${testId}-scrim`}
      />

      {/* Centred content + bottom CTA. The entrance beat is motion-safe
          (reduced-motion users get it static). */}
      <div
        className={cn(
          "relative z-10 mx-auto flex min-h-svh w-full max-w-md flex-col items-center px-6 pb-8 text-center text-white",
          MOTION_BEATS["brand-intro"],
        )}
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          {/* White logo card + accent check badge. */}
          <div className="relative" data-testid={`${testId}-logo-card`}>
            <div className="flex size-32 items-center justify-center rounded-3xl bg-white shadow-xl">
              <BrandLogo
                logoUrl={brandLogoUrl}
                name={brandName}
                className="size-20 rounded-2xl"
                monogramClassName="text-3xl"
                testId={`${testId}-brand-logo`}
              />
            </div>
            <span
              className="bg-primary absolute -right-2 -bottom-2 flex size-10 items-center justify-center rounded-full ring-4 ring-black/20"
              role="img"
              aria-label={t("signup_arrival.check_alt")}
              data-testid={`${testId}-check`}
            >
              <Check className="size-5 text-white" strokeWidth={3} />
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <h1
              ref={headingRef}
              tabIndex={-1}
              data-testid={`${testId}-heading`}
              className="text-2xl font-bold tracking-tight text-balance outline-none"
            >
              {t("signup_arrival.heading", { brand: brandName })}
            </h1>
            <p
              data-testid={`${testId}-body`}
              className="text-base text-pretty text-white/80"
            >
              {t("signup_arrival.body", { name: creatorName })}
            </p>
          </div>
        </div>

        <Button
          asChild
          size="lg"
          className="w-full"
          data-testid={`${testId}-cta`}
        >
          <Link href={creatorMissionsPath()}>{t("signup_arrival.cta")}</Link>
        </Button>
      </div>
    </div>
  )
}

export default SuccessArrival
